import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { bugService } from '../services/bug.service.js';
import { useAuth } from './AuthContext.jsx';

const BugReportContext = createContext(null);

const EMPTY_COUNTS = { all: 0, open: 0, in_progress: 0, resolved: 0, closed: 0 };
const EMPTY_UNREAD = { total: 0, threads: 0 };

// Poll so a reply sent from the other side shows up without a page reload
const UNREAD_POLL_MS = 45000;

/**
 * Shared bug report counts
 * Keeps the sidebar badge in sync with the report form and the bug list
 */
export const useBugReports = () => {
  const context = useContext(BugReportContext);
  if (!context) {
    throw new Error('useBugReports must be used within a BugReportProvider');
  }
  return context;
};

export const BugReportProvider = ({ children }) => {
  const { user } = useAuth();
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  // Replies the logged-in user has not opened yet
  const [unread, setUnread] = useState(EMPTY_UNREAD);
  const pollTimerRef = useRef(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const userId = user?._id || null;

  const refreshCounts = useCallback(async () => {
    if (!userId) {
      setCounts(EMPTY_COUNTS);
      setUnread(EMPTY_UNREAD);
      return;
    }

    try {
      const response = await bugService.getBugCounts();
      if (response.success) {
        setCounts({ ...EMPTY_COUNTS, ...(response.data?.counts || {}) });
        setUnread({ ...EMPTY_UNREAD, ...(response.data?.unread || {}) });
      }
    } catch (err) {
      console.error('Error fetching bug counts:', err);
    }
  }, [userId]);

  /**
   * Mark one thread as read - used when the user opens a conversation
   */
  const markThreadRead = useCallback(
    async (bugId) => {
      if (!bugId || !userId) return;

      try {
        const response = await bugService.markBugRead(bugId);
        if (response.success) {
          setUnread({ ...EMPTY_UNREAD, ...(response.data?.unread || {}) });
        }
      } catch (err) {
        console.error('Error marking bug report as read:', err);
      }
    },
    [userId]
  );

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  // Keep the unread badge fresh while the tab is in use
  useEffect(() => {
    if (!userId) return;

    const startPolling = () => {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = setInterval(refreshCounts, UNREAD_POLL_MS);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(pollTimerRef.current);
      } else {
        // Catch up immediately on coming back to the tab
        refreshCounts();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(pollTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [userId, refreshCounts]);

  return (
    <BugReportContext.Provider
      value={{
        counts,
        openCount: isAdmin ? counts.open || 0 : 0,
        unreadCount: unread.total || 0,
        unreadThreads: unread.threads || 0,
        refreshCounts,
        markThreadRead,
      }}
    >
      {children}
    </BugReportContext.Provider>
  );
};

export default BugReportContext;
